import { useState, useEffect } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { Search, MapPin, Calendar, AlertTriangle, FileText, Download, X, ChevronRight, Bell, Copy, Check } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import {
  PLANNING_FILTERS_ID,
  PLANNING_STICKY_TOP_CLASS,
  scheduleScrollToPlanningFilters,
} from '../constants/planning';

const PLANNING_ALERT_BACK_ID = 'planning-alert-back';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scrollToStayUpdated = () => {
  const section = document.querySelector('[data-testid="main-footer"]');
  if (!section) return;
  const header = document.querySelector('[data-testid="main-header"]');
  const offset = header?.getBoundingClientRect().height ?? 104;
  const top = section.getBoundingClientRect().top + window.pageYOffset - offset;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({
    top: Math.max(0, top),
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
  });
};

const categoryConfig = {
  residential: { label: 'Residential', color: 'bg-blue-500 text-white' },
  commercial: { label: 'Commercial', color: 'bg-purple-500 text-white' },
  infrastructure: { label: 'Infrastructure', color: 'bg-cpre-autumn-leaf text-white' },
  environmental: { label: 'Environmental', color: 'bg-cpre-new-yew text-white' },
};

const categories = [
  { value: 'all', label: 'All Categories' },
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'environmental', label: 'Environmental' },
];

const PlanningPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [copiedTemplate, setCopiedTemplate] = useState(false);
  
  const categoryFilter = searchParams.get('category') || 'all';

  useEffect(() => {
    if (location.hash !== '#planning-filters') return;
    return scheduleScrollToPlanningFilters({ delayMs: 50 });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!selectedAlert) return;
    const timeoutId = window.setTimeout(() => {
      const back = document.getElementById(PLANNING_ALERT_BACK_ID);
      const header = document.querySelector('[data-testid="main-header"]');
      const filters = document.getElementById(PLANNING_FILTERS_ID);
      if (!back) return;
      const offset =
        (header?.getBoundingClientRect().height ?? 104) +
        (filters?.getBoundingClientRect().height ?? 72);
      const top = back.getBoundingClientRect().top + window.pageYOffset - offset;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    }, 50);
    return () => window.clearTimeout(timeoutId);
  }, [selectedAlert]);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await axios.get(`${API}/planning-alerts`);
        setAlerts(response.data || []);
      } catch (error) {
        console.error('Failed to fetch alerts:', error);
        setAlerts([
          {
            id: '1',
            title: 'Solar Farm Application - Stamford Area',
            summary: 'Application for a 50MW solar farm on agricultural land between Stamford and Uffington. The proposed development covers approximately 100 hectares and includes solar panels, battery storage facilities, and associated infrastructure.',
            impact: 'This application would result in the loss of 100 hectares of Grade 2 agricultural land, which is some of the best and most versatile farmland in the country. The development would have a significant visual impact on the rolling countryside, affecting views from multiple public rights of way and the nearby village of Uffington. There are also concerns about the impact on local wildlife, including ground-nesting birds.',
            how_to_respond: 'Submit your objection to South Kesteven District Council by the deadline. You can submit online through the council\'s planning portal, or write to the Planning Department. When objecting, focus on material planning considerations such as landscape impact, loss of agricultural land, and effects on wildlife. Personal opinions about solar energy are not material planning considerations.',
            location: 'Stamford, Lincolnshire',
            category: 'environmental',
            reference_number: 'S26/0142',
            deadline: '2026-02-28T23:59:00Z',
            template_letter: `Dear Planning Officer,

Re: Application S26/0142 - Solar Farm, Stamford Area

I am writing to object to the above planning application for a 50MW solar farm on agricultural land between Stamford and Uffington.

My objections are based on the following material planning considerations:

1. LOSS OF AGRICULTURAL LAND
The proposed development would result in the permanent loss of approximately 100 hectares of Grade 2 agricultural land. This is classified as "best and most versatile" farmland under the NPPF and should be protected from development.

2. LANDSCAPE IMPACT
The scale of this development would have a significant adverse impact on the character of the local landscape. The site is visible from multiple public rights of way and the nearby village of Uffington.

3. WILDLIFE CONCERNS
The site provides important habitat for ground-nesting birds and other wildlife. Insufficient consideration has been given to the ecological impact of this development.

I urge the Council to refuse this application.

Yours faithfully,
[Your name and address]`,
          },
          {
            id: '2',
            title: 'Housing Development - Oakham South',
            summary: 'Outline application for 150 homes on greenfield land south of Oakham, including affordable housing provision, public open space, and new access roads.',
            impact: 'Development on greenfield site outside the current local plan allocation. The proposal would increase traffic on already congested local roads, particularly during peak hours. The site provides important green separation between Oakham and surrounding villages. There are concerns about the capacity of local schools and healthcare facilities to accommodate additional residents.',
            how_to_respond: 'Contact Rutland County Council planning department. You can view the full application documents on their website and submit comments online. The council also accepts written representations by post.',
            location: 'Oakham, Rutland',
            category: 'residential',
            reference_number: 'R26/0089',
            deadline: '2026-03-15T23:59:00Z',
            template_letter: `Dear Planning Officer,

Re: Application R26/0089 - Housing Development, Oakham South

I wish to register my objection to the above outline planning application for 150 homes on greenfield land south of Oakham.

My objections are based on the following material planning considerations:

1. NOT IN LOCAL PLAN
This site is not allocated for development in the current Local Plan. Development here would be premature and undermine the plan-led system.

2. TRAFFIC IMPACT
The local road network is already at capacity during peak hours. This development would add approximately 300 additional vehicle movements per day, causing further congestion.

3. LOSS OF GREEN SEPARATION
The site provides important green separation between Oakham and surrounding villages. Development here would harm the distinct character of these settlements.

4. INFRASTRUCTURE CAPACITY
Local schools and GP surgeries are already at capacity. No adequate provision has been made to address this.

I request that this application be refused.

Yours faithfully,
[Your name and address]`,
          },
          {
            id: '3',
            title: 'Warehouse Development - A1 Corridor',
            summary: 'Full application for large distribution warehouse with associated parking and HGV facilities near the A1.',
            impact: 'The proposed warehouse would be visible from a wide area and would industrialise what is currently an agricultural landscape. HGV traffic would increase significantly on local roads. Light pollution from 24-hour operations would affect nearby residents and wildlife.',
            how_to_respond: 'Submit comments to the local planning authority within the consultation period. Attend the planning committee meeting when the application is considered.',
            location: 'Grantham, Lincolnshire',
            category: 'commercial',
            reference_number: 'S26/0198',
            deadline: '2026-03-01T23:59:00Z',
            template_letter: `Dear Planning Officer,

Re: Application S26/0198 - Warehouse Development, A1 Corridor

I object to the above planning application for a distribution warehouse near the A1.

My objections include:

1. LANDSCAPE HARM
The scale and industrial nature of this development would cause significant harm to the rural character of the area.

2. TRAFFIC
HGV movements would increase substantially, causing noise, pollution, and safety concerns on local roads.

3. LIGHT POLLUTION
24-hour operations would cause unacceptable light pollution affecting residents and wildlife.

I urge refusal of this application.

Yours faithfully,
[Your name and address]`,
          },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchAlerts();
  }, []);

  const filteredAlerts = alerts.filter((alert) => {
    if (categoryFilter !== 'all' && alert.category !== categoryFilter) return false;
    if (search !== '') {
      const q = search.toLowerCase();
      const matchesTitle = alert.title?.toLowerCase().includes(q);
      const matchesSummary = alert.summary?.toLowerCase().includes(q);
      const matchesLocation = alert.location?.toLowerCase().includes(q);
      if (!matchesTitle && !matchesSummary && !matchesLocation) return false;
    }
    return true;
  });

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    setSearchParams(newParams);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No deadline';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const getDaysRemaining = (deadline) => {
    if (!deadline) return null;
    const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const handleCopyTemplate = () => {
    if (selectedAlert?.template_letter) {
      navigator.clipboard.writeText(selectedAlert.template_letter);
      setCopiedTemplate(true);
      toast.success('Template letter copied to clipboard!');
      setTimeout(() => setCopiedTemplate(false), 3000);
    }
  };

  const handleDownloadTemplate = () => {
    if (selectedAlert?.template_letter) {
      const blob = new Blob([selectedAlert.template_letter], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `objection-letter-${selectedAlert.reference_number || 'template'}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Template letter downloaded!');
    }
  };

  return (
    <Layout>
      <div data-testid="planning-page">
        {/* Hero Section */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-cpre-new-yew">
          <div className="absolute inset-0 opacity-20">
            <img
              src="/images/cpre images for website/2.jpg"
              alt="Planning"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative container-custom">
            <Breadcrumbs variant="light" customItems={[{ label: 'Planning Alerts' }]} />
            <div className="text-center text-white mt-8">
              <Badge className="bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white mb-4">
                <AlertTriangle className="w-4 h-4 mr-1" />
                Stay Informed
              </Badge>
              <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4 text-white">Planning Alerts</h1>
              <p className="text-xl text-white/90 max-w-2xl mx-auto">
                Monitor planning applications that could impact the countryside of Rutland and Lincolnshire.
              </p>
            </div>
          </div>
        </section>

        {/* Filters */}
        <div
          id={PLANNING_FILTERS_ID}
          className="scroll-mt-[6.5rem] md:scroll-mt-[7.5rem]"
        >
          <div
            className={`sticky ${PLANNING_STICKY_TOP_CLASS} z-40 bg-white border-b border-stone-200 shadow-sm`}
          >
            <div className="container-custom py-4 md:py-5">
            <div
              className="flex flex-col md:flex-row gap-4 md:gap-6 items-center justify-between"
              role="toolbar"
              aria-label="Filter planning alerts"
            >
              <div className="flex gap-3 w-full md:w-auto min-w-0">
                <Select value={categoryFilter} onValueChange={(v) => updateFilter('category', v)}>
                  <SelectTrigger className="w-[180px] rounded-lg" data-testid="filter-category">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="relative w-full md:w-96 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search alerts by location or keyword..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 rounded-lg w-full"
                  data-testid="search-alerts"
                />
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* Alerts Content */}
        <section className="section-padding pt-8 md:pt-10">
          <div className="container-custom">
            {selectedAlert ? (
              /* Full Alert Detail View */
              <div data-testid="alert-detail-full">
                <div id={PLANNING_ALERT_BACK_ID} className="mb-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSelectedAlert(null)}
                    className="inline-flex w-auto text-cpre-new-yew border-stone-300 bg-white hover:bg-cpre-pebble/40 hover:text-cpre-new-yew shadow-sm [&_svg]:text-cpre-new-yew"
                    data-testid="back-to-all-alerts"
                  >
                    <ChevronRight className="w-4 h-4 mr-2 rotate-180" />
                    Back to all alerts
                  </Button>
                </div>

                {/* Alert Header */}
                <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 mb-8">
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <Badge className={`${categoryConfig[selectedAlert.category]?.color} rounded-full`}>
                      {categoryConfig[selectedAlert.category]?.label}
                    </Badge>
                    {selectedAlert.reference_number && (
                      <Badge variant="outline" className="text-muted-foreground">
                        Ref: {selectedAlert.reference_number}
                      </Badge>
                    )}
                    {getDaysRemaining(selectedAlert.deadline) > 0 && (
                      <Badge variant="outline" className={`${getDaysRemaining(selectedAlert.deadline) <= 7 ? 'border-red-500 text-red-500' : 'border-cpre-autumn-leaf text-cpre-autumn-leaf'}`}>
                        {getDaysRemaining(selectedAlert.deadline)} days to respond
                      </Badge>
                    )}
                  </div>
                  <h1 className="text-3xl font-heading font-bold mb-4">{selectedAlert.title}</h1>
                  <div className="flex flex-wrap items-center gap-6 text-muted-foreground">
                    <span className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-cpre-new-yew" />
                      {selectedAlert.location}
                    </span>
                    {selectedAlert.deadline && (
                      <span className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-cpre-autumn-leaf" />
                        Deadline: {formatDate(selectedAlert.deadline)}
                      </span>
                    )}
                  </div>
                </div>

                {/* 1. SUMMARY SECTION */}
                <div className="mb-8" data-testid="alert-summary">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-8 bg-cpre-new-yew rounded-full" />
                    <h2 className="text-2xl font-heading font-semibold">Summary</h2>
                  </div>
                  <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8">
                    <p className="text-foreground leading-relaxed text-lg">
                      {selectedAlert.summary}
                    </p>
                  </div>
                </div>

                {/* 2. IMPACT STATEMENT SECTION */}
                <div className="mb-8" data-testid="alert-impact">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-8 bg-cpre-autumn-leaf rounded-full" />
                    <h2 className="text-2xl font-heading font-semibold">Impact Statement</h2>
                  </div>
                  <div className="bg-cpre-autumn-leaf/5 rounded-2xl border border-cpre-autumn-leaf/20 p-8">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-cpre-autumn-leaf/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-6 h-6 text-cpre-autumn-leaf" />
                      </div>
                      <p className="text-foreground leading-relaxed">
                        {selectedAlert.impact}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 3. HOW TO RESPOND SECTION */}
                <div className="mb-8" data-testid="alert-respond">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-8 bg-cpre-hill rounded-full" />
                    <h2 className="text-2xl font-heading font-semibold">How to Respond</h2>
                  </div>
                  <div className="bg-cpre-mint/10 rounded-2xl border border-cpre-hill/20 p-8">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-cpre-hill/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <FileText className="w-6 h-6 text-cpre-hill" />
                      </div>
                      <div>
                        <p className="text-foreground leading-relaxed mb-4">
                          {selectedAlert.how_to_respond}
                        </p>
                        {selectedAlert.deadline && (
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cpre-harvest-gold/20 text-cpre-new-yew rounded-full font-medium">
                            <Calendar className="w-4 h-4" />
                            Response deadline: {formatDate(selectedAlert.deadline)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 4. TEMPLATE LETTER DOWNLOAD SECTION */}
                {selectedAlert.template_letter && (
                  <div className="mb-8" data-testid="alert-template">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-1 h-8 bg-cpre-harvest-gold rounded-full" />
                      <h2 className="text-2xl font-heading font-semibold">Template Letter</h2>
                    </div>
                    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
                      <div className="p-6 border-b border-stone-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-lg">Objection Letter Template</h3>
                          <p className="text-muted-foreground text-sm">Use this template as a starting point for your response</p>
                        </div>
                        <div className="flex gap-3">
                          <Button 
                            variant="outline"
                            onClick={handleCopyTemplate}
                            className="border-cpre-new-yew text-cpre-new-yew hover:bg-cpre-new-yew hover:text-white"
                            data-testid="copy-template"
                          >
                            {copiedTemplate ? (
                              <>
                                <Check className="w-4 h-4 mr-2" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4 mr-2" />
                                Copy to Clipboard
                              </>
                            )}
                          </Button>
                          <Button 
                            onClick={handleDownloadTemplate}
                            className="bg-cpre-new-yew hover:bg-cpre-hill text-white"
                            data-testid="download-template"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      </div>
                      <div className="p-6 bg-cpre-pebble/20">
                        <pre className="whitespace-pre-wrap font-sans text-sm text-foreground leading-relaxed">
                          {selectedAlert.template_letter}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Support CTA */}
                <div className="bg-cpre-new-yew rounded-2xl p-8 text-white text-center">
                  <Bell className="w-12 h-12 mx-auto mb-4 text-cpre-mint" />
                  <h3 className="font-heading font-bold text-2xl mb-2 text-white">Stay Informed</h3>
                  <p className="text-white/80 mb-6 max-w-xl mx-auto">
                    Subscribe to planning alerts for your area and never miss an important application.
                  </p>
                  <Button size="lg" className="bg-cpre-autumn-leaf hover:bg-cpre-autumn-leaf/90 text-white rounded-full px-8">
                    Subscribe to Alerts
                  </Button>
                </div>
              </div>
            ) : (
              /* Alerts List View */
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Alerts List */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-heading font-semibold">
                      {filteredAlerts.length} Active Alert{filteredAlerts.length !== 1 ? 's' : ''}
                    </h2>
                  </div>
                  
                  {loading ? (
                    [1, 2, 3].map(i => (
                      <div key={i} className="h-48 bg-stone-100 rounded-2xl animate-pulse" />
                    ))
                  ) : filteredAlerts.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
                      <AlertTriangle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No planning alerts found matching your criteria.</p>
                    </div>
                  ) : (
                    filteredAlerts.map((alert, index) => {
                      const category = categoryConfig[alert.category] || categoryConfig.environmental;
                      const daysRemaining = getDaysRemaining(alert.deadline);

                      return (
                        <Card 
                          key={alert.id}
                          className="bg-white border-stone-200 overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-cpre-new-yew/30"
                          onClick={() => setSelectedAlert(alert)}
                          data-testid={`alert-card-${index}`}
                        >
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between gap-4 mb-4">
                              <div>
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                  <Badge className={`${category.color} rounded-full text-xs font-medium`}>
                                    {category.label}
                                  </Badge>
                                  {alert.reference_number && (
                                    <Badge variant="outline" className="text-xs">
                                      {alert.reference_number}
                                    </Badge>
                                  )}
                                </div>
                                <h3 className="font-heading font-semibold text-xl text-foreground">
                                  {alert.title}
                                </h3>
                              </div>
                              {daysRemaining !== null && daysRemaining > 0 && (
                                <Badge variant="outline" className={`whitespace-nowrap flex-shrink-0 ${daysRemaining <= 7 ? 'border-red-500 text-red-500 bg-red-50' : ''}`}>
                                  {daysRemaining} days left
                                </Badge>
                              )}
                            </div>
                            <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                              {alert.summary}
                            </p>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {alert.location}
                                </span>
                              </div>
                              <span className="text-cpre-new-yew text-sm font-medium flex items-center gap-1">
                                View details
                                <ChevronRight className="w-4 h-4" />
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Subscribe Card */}
                  <Card className="bg-cpre-new-yew text-white border-0">
                    <CardContent className="p-6 text-center">
                      <Bell className="w-12 h-12 mx-auto mb-4 text-cpre-mint" />
                      <h3 className="font-heading font-semibold text-xl mb-2 text-white">Get Alerts</h3>
                      <p className="text-white/80 text-sm mb-4">
                        Subscribe to receive planning alerts for your local area.
                      </p>
                      <Button
                        type="button"
                        className="w-full bg-white text-cpre-new-yew hover:bg-cpre-mint rounded-lg"
                        onClick={scrollToStayUpdated}
                      >
                        Subscribe
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Quick Info */}
                  <Card className="bg-cpre-pebble/30 border-0">
                    <CardContent className="p-6">
                      <h3 className="font-heading font-semibold text-lg mb-4">How We Can Help</h3>
                      <ul className="space-y-3 text-sm">
                        <li className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-cpre-new-yew/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-cpre-new-yew" />
                          </div>
                          <span>Monitor planning applications</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-cpre-new-yew/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-cpre-new-yew" />
                          </div>
                          <span>Provide template objection letters</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-cpre-new-yew/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-cpre-new-yew" />
                          </div>
                          <span>Advise on how to respond</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-cpre-new-yew/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-cpre-new-yew" />
                          </div>
                          <span>Campaign against harmful developments</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default PlanningPage;
